import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@/environments/environment';
import { AuthService } from '@/app/services/auth.service';

/**
 * Canonical AI Assist icon (PrimeIcons). The single source of truth — reference
 * it everywhere ai-assist appears (buttons, widget, dialogs) so the icon stays
 * consistent. For sidebar menu items use `AI_ASSIST_MENU_ICON` (adds the
 * fixed-width `pi-fw` modifier for alignment with the other menu entries).
 */
export const AI_ASSIST_ICON = 'pi pi-microchip-ai';
export const AI_ASSIST_MENU_ICON = 'pi pi-fw pi-microchip-ai';

/** Error envelope from the AI Assist service: HTTP 400 + `{ code }` (overview §6). */
export interface AiAssistErrorBody {
    code: string;
    message?: string;
}

/** Body of `POST /health` — liveness, vendor reachability, DB, in-flight jobs. */
export interface AiAssistHealth {
    status: string;
    version: string;
    vendors_reachable: { grok: boolean; flux: boolean; recraft: boolean };
    db: { reachable: boolean };
    active_jobs: number;
}

// Stable vendor identifiers shared with the AI Assist service balance API.
export const VENDOR_FLUX = 'flux';
export const VENDOR_RECRAFT = 'recraft';
export const VENDOR_GROK = 'grok';

/**
 * Vendor billing consoles — where the operator tops up credits. Opened in a new
 * tab from the credit indicators. Recraft has no stable billing deep-link, so
 * we land on the app (profile → Manage subscription from there). Adjust here if
 * a vendor changes its console URL.
 */
export const VENDOR_DASHBOARD_URLS: Record<string, string> = {
    [VENDOR_FLUX]: 'https://dashboard.bfl.ai',
    [VENDOR_RECRAFT]: 'https://www.recraft.ai/',
    [VENDOR_GROK]: 'https://console.x.ai/team/default/billing',
};

/**
 * Per-vendor credit-balance health (mirrors the service's `BalanceStatus`):
 * - `ok`           — comfortably above the warning threshold
 * - `low`          — below the warning threshold; operator should top up
 * - `critical`     — below the block threshold; generations are refused
 * - `unknown`      — the balance fetch failed (service fails open — no block)
 * - `unconfigured` — no balance source wired (e.g. Grok in v1)
 */
export type VendorBalanceStatus = 'ok' | 'low' | 'critical' | 'unknown' | 'unconfigured';

/** One vendor's credit balance + derived status (`POST /vendors/balance`). */
export interface VendorBalance {
    vendor: string; // 'flux' | 'recraft' | 'grok'
    status: VendorBalanceStatus;
    /** Remaining credits in `unit`; null when unknown / unconfigured. */
    balance: number | null;
    unit: string; // 'credits' | 'usd'
    low_threshold: number | null;
    critical_threshold: number | null;
    detail?: string | null;
}

/** Body of `POST /vendors/balance` — per-vendor balances + roll-up flags. */
export interface VendorBalancesResponse {
    vendors: VendorBalance[];
    /** At least one configured vendor is `critical` (drives the red dot). */
    any_critical: boolean;
    /** At least one configured vendor is `low` (drives the amber dot). */
    any_low: boolean;
}

/**
 * Overall service status for the topbar indicator dot:
 * - `ok`        — reachable, DB up, all vendors reachable (green)
 * - `degraded`  — reachable but something is off (DB or a vendor down) (yellow)
 * - `down`      — the service did not answer (red)
 * - `unknown`   — not probed yet (grey)
 */
export type AiAssistStatus = 'ok' | 'degraded' | 'down' | 'unknown';

/** A single async job in the operator's widget (`POST /asset/jobs/mine`). */
export interface AiAssistJob {
    job_id: string;
    kind: string;
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    /** Populated only for `failed` jobs — the §6 error code + message. */
    error?: AiAssistErrorBody | null;
    created_at: string;
    completed_at?: string | null;
}

/** Body of `POST /asset/jobs/mine`. */
export interface MyJobsResponse {
    jobs: AiAssistJob[];
    active: number;
}

/** Token accounting returned by the Contract 4 text helpers. */
export interface AiAssistTokensUsed {
    input: number;
    output: number;
    reasoning: number;
}

/** Body of `POST /text/refine` (Contract 4 §3). */
export interface RefineRequest {
    /** The text to rewrite / improve (1–4000 chars). */
    text: string;
    /** Plain-language instruction, e.g. "fix grammar, keep tone playful". */
    instruction: string;
    /** Optional hard cap on the refined length (1–4000). */
    maxLength?: number | null;
}

/** Response of `POST /text/refine`. */
export interface RefineResponse {
    request_id: string;
    refined: string;
    alternatives: string[];
    vendor: string;
    tokens_used: AiAssistTokensUsed;
    cost_usd: number;
    latency_ms: number;
}

/**
 * Approved brand values forwarded so the SEO copy stays consistent
 * (Contract 4 §2).
 */
export interface BrandingContext {
    displayName?: string | null;
    name?: string | null;
}

/**
 * Body of `POST /text/suggest-branding` (camelCase; mapped to wire on send).
 * AI drafts the SEO meta description only — names are operator-supplied, the
 * logo split is admin/manual, About/Disclaimer are category boilerplate.
 */
export interface SuggestBrandingRequest {
    brandName: string;
    domain: string;
    /** Tone hint derived from the platform category (`skill_games` / `social_casino`). */
    vertical?: string | null;
    /** Website-template StyleDescriptor — opaque; only a personality hint is used. */
    style?: Record<string, unknown> | null;
    context?: BrandingContext;
    operatorNote?: string | null;
}

/** Drafts payload of `POST /text/suggest-branding`. */
export interface BrandingDrafts {
    seo_description: string[];
}

/** Response of `POST /text/suggest-branding`. */
export interface SuggestBrandingResponse {
    request_id: string;
    drafts: BrandingDrafts;
    vendor: string;
    tokens_used: AiAssistTokensUsed;
    cost_usd: number;
    latency_ms: number;
}

// ---------------------------------------------------------------------------
// Async image jobs (Contracts 1 & 2) — `POST` returns 202 + job_id; the result
// is delivered by polling `POST /asset/jobs/{job_id}/status`. No callbacks.
// ---------------------------------------------------------------------------

export type JobStatus = 'pending' | 'completed' | 'failed' | 'cancelled';
export type AssetJobKind = 'base_art' | 'asset_finalize' | 'pages';

/** 202 body from the async `POST /asset/...` generate / finalize endpoints. */
export interface JobAccepted {
    request_id: string;
    job_id: string;
    status: JobStatus;
    estimated_ready_in_seconds: number | null;
}

/** Polling response — `result` shape depends on `kind` (cast accordingly). */
export interface JobStatusResponse {
    job_id: string;
    kind: AssetJobKind;
    status: JobStatus;
    result: Record<string, unknown> | null;
    error: AiAssistErrorBody | null;
}

export type BaseArtAssetType = 'icon' | 'banner' | 'brand_icon';
export type GenerateMode = 'manual' | 'auto';

/** The downscaled favicon rendition shipped with each `brand_icon` candidate (A2). */
export interface BaseArtFaviconVariant {
    url: string;
    width: number;
    height: number;
    format: string;
    size_bytes: number;
}

export interface BaseArtCandidate {
    url: string;
    seed: number | null;
    width: number;
    height: number;
    format: string;
    size_bytes: number;
    /** brand_icon only — same mark downscaled for the favicon; else null. */
    favicon: BaseArtFaviconVariant | null;
}

/** `result` payload of a completed `base_art` job (Contract 1). */
export interface BaseArtResult {
    asset_type: BaseArtAssetType;
    scope: {
        template_id: string;
        version: string;
        game_template_id: string | null;
        game_kind: string | null;
    };
    candidates: BaseArtCandidate[];
    composed_prompt: string;
    authored_prompt: string | null;
    mode: GenerateMode;
    vendor: string;
    latency_ms: number;
    cost_credits: number;
}

/** Body of `POST /asset/base/generate` (camelCase; mapped to wire on send). */
export interface BaseArtGenerateRequest {
    /** Client-generated UUID — idempotency key (re-send = same job). */
    jobId: string;
    /**
     * Realm scope. REQUIRED for `brand_icon` (realm-level, PBE-24). For game
     * plates (`icon`/`banner`) the art is realm-independent (PBE-22, keyed by
     * game-template × website-template) — omit it; the service authorizes those
     * on global-admin instead.
     */
    realmId?: string | null;
    /** Website-template id + style version. Manual until templates are wired in. */
    templateId: string;
    version: string;
    /** Text-free brand references (1–8) as public URLs. Provide URLs OR base64. */
    referenceImageUrls?: string[];
    /** Text-free brand references as inline base64 (e.g. read via getSiteTemplateFile). */
    referenceImagesB64?: string[];
    assetType: BaseArtAssetType;
    styleDescriptor?: Record<string, unknown> | null;
    gameTemplateId?: string | null;
    gameKind?: string | null;
    gameMeta?: { name: string; description?: string } | null;
    /** `manual` requires `prompt`; `auto` authors the prompt from style/meta. */
    mode?: GenerateMode;
    prompt?: string | null;
    /**
     * "Semi-auto": extra operator detail folded into the Grok-authored prompt in
     * `auto` mode (Grok composes game + style + this note). Ignored in `manual`.
     */
    operatorNote?: string | null;
    samples?: number;
}

export type FinalizeAssetType = 'icon' | 'banner' | 'og';

export interface FinalizeCandidate {
    url: string;
    seed: number | null;
    width: number;
    height: number;
    format: string;
    size_bytes: number;
    /**
     * icon/banner (v0.7): the transparent logo PNG behind this candidate. Reuse
     * it on the next format (banner) via `FinalizeImageRequest.letteringUrl` so
     * both formats share one logo. Null for og and reused-logo calls.
     */
    lettering_url?: string | null;
}

/** `result` payload of a completed `asset_finalize` job (Contract 2). */
export interface FinalizeResult {
    asset_type: FinalizeAssetType;
    title: string;
    candidates: FinalizeCandidate[];
    base_plate_url: string;
    composed_prompt: string;
    /** `flux2_pro_compose` (icon/banner) or `flux2_pro_edit` (og). */
    vendor: string;
    latency_ms: number;
    cost_credits: number;
    /** Grok authoring cost (USD) — telemetry; FLUX/Recraft credits are in cost_credits. */
    grok_cost_usd?: number;
}

/** Body of `POST /asset/finalize` (camelCase; mapped to wire on send). */
export interface FinalizeImageRequest {
    jobId: string;
    /**
     * Realm scope. Realm-level finalized assets (e.g. an OG image) carry it.
     * Game-template default icon/banner (no realm) omit it — authorized on
     * global-admin (Contract 2 v0.6).
     */
    realmId?: string | null;
    /** The text-free plate to overlay the name onto, as a public URL. Provide a URL OR base64. */
    basePlateUrl?: string | null;
    /** The text-free plate as inline base64 (e.g. read via getSiteTemplateFile). */
    basePlateB64?: string | null;
    assetType: FinalizeAssetType;
    title: string;
    /** Required for `og`. */
    tagline?: string | null;
    /**
     * Brand kit `*.style.json` (v0.7). Grok authors the logo treatment
     * (icon/banner) / OG prompt from it — pass the reference template's style.
     */
    styleDescriptor?: Record<string, unknown> | null;
    /**
     * icon/banner (v0.7): reuse a pre-generated logo (a candidate's
     * `lettering_url` from a prior icon job) so banner matches icon. Provide a
     * URL OR base64. `og` rejects both.
     */
    letteringUrl?: string | null;
    letteringB64?: string | null;
    titleSeed?: number | null;
    samples?: number;
}

// ── Contract 3 — Pages (storyboard) ─────────────────────────────────────────

export type PageScene =
    | 'feature_showcase'
    | 'social_multiplayer'
    | 'promo_bonus'
    | 'gameplay_hook'
    | 'reward_payout'
    | 'custom';

export interface PageCandidate {
    url: string;
    seed: number | null;
    width: number;
    height: number;
    format: string;
    size_bytes: number;
}

export interface PageResultItem {
    index: number;
    scene: PageScene;
    cta_text: string;
    copy_source: 'operator' | 'grok';
    candidates: PageCandidate[];
}

/** `result` payload of a completed `pages` job (Contract 3). */
export interface PagesResult {
    title: string;
    pages: PageResultItem[];
    scene_source: 'operator' | 'grok';
    composed_prompts: string[];
    vendor: string;
    latency_ms: number;
    cost_credits: number;
}

export interface PagesGenerateRequest {
    jobId: string;
    /** Optional — game-template default pages carry no realm (global-admin). */
    realmId?: string | null;
    templateId: string;
    version: string;
    styleDescriptor?: Record<string, unknown> | null;
    /** Brand references — provide URLs OR base64. */
    referenceImageUrls?: string[];
    referenceImagesB64?: string[];
    /** The game's icon/banner base plates as extra references — URLs OR base64. */
    gameBaseUrls?: string[];
    gameBaseB64?: string[];
    /**
     * The finalized title wordmark (from the `iconBanner` variant) to composite
     * as the page title — URL or base64. Keeps the logo pixel-consistent with
     * the game's icon/banner. Optional.
     */
    letteringUrl?: string | null;
    letteringB64?: string | null;
    gameTemplateId: string;
    gameKind: string;
    gameMeta?: { name: string; description: string };
    title: string;
    sceneMode?: 'auto' | 'manual';
    pageCount?: number;
    copyMode?: 'auto' | 'manual';
    samples?: number;
}

/** Thrown when the service answers with its 400 error envelope. */
export class AiAssistApiError extends Error {
    constructor(
        readonly code: string,
        message?: string,
    ) {
        super(message ?? code);
        this.name = 'AiAssistApiError';
    }
}

/**
 * Thin client for the Platform Admin AI Assist Service (a separate backend from
 * the platform Admin API — it does NOT go through lib-backend). POST-only.
 * Auth: forwards the admin Bearer token, which the service validates via the
 * existing `admin/auth/me` introspection. The base URL is relative and routed
 * to the shared service instance by the reverse proxy (published) or the
 * ng-serve proxy (local).
 */
@Injectable({ providedIn: 'root' })
export class AiAssistService {
    private readonly baseUrl = environment.aiAssistBaseUrl;
    private readonly auth = inject(AuthService);

    /** Latest `/health` payload, or null if the last probe failed / none yet. */
    readonly health = signal<AiAssistHealth | null>(null);

    /** Derived traffic-light status for the topbar dot. */
    readonly status = signal<AiAssistStatus>('unknown');

    /** True while a `/health` probe is in flight (drives the popover skeleton). */
    readonly healthLoading = signal(false);

    /** The current operator's recent jobs (topbar widget). */
    readonly jobs = signal<AiAssistJob[]>([]);

    /** Count of the operator's active (pending) jobs. */
    readonly jobsActive = signal(0);

    /** False when the last `myJobs` probe failed (→ show "couldn't load"). */
    readonly jobsLoaded = signal(false);

    /** Latest vendor credit balances, or null if the last probe failed / none yet. */
    readonly vendorBalances = signal<VendorBalancesResponse | null>(null);

    /** False when the last balance probe failed (→ show "couldn't load"). */
    readonly vendorBalancesLoaded = signal(false);

    /** True while a balance probe is in flight (drives the popover skeleton). */
    readonly vendorBalancesLoading = signal(false);

    /**
     * Probe `POST /health` (no auth) and update `health` + `status`. Never
     * throws — a failed probe is reported as `down`.
     */
    async refreshHealth(): Promise<void> {
        this.healthLoading.set(true);
        try {
            const h = await this.post<AiAssistHealth>('health', {}, { auth: false });
            this.health.set(h);
            const vendorsOk =
                h.vendors_reachable.grok && h.vendors_reachable.flux && h.vendors_reachable.recraft;
            this.status.set(h.db.reachable && vendorsOk ? 'ok' : 'degraded');
        } catch {
            this.health.set(null);
            this.status.set('down');
        } finally {
            this.healthLoading.set(false);
        }
    }

    /**
     * Probe `POST /asset/jobs/mine` (admin auth) and update `jobs` /
     * `jobsActive` / `jobsLoaded`. Never throws — a failed probe clears the
     * list and marks `jobsLoaded` false.
     */
    async refreshMyJobs(opts?: {
        statuses?: AiAssistJob['status'][];
        limit?: number;
    }): Promise<void> {
        try {
            const res = await this.post<MyJobsResponse>('asset/jobs/mine', {
                statuses: opts?.statuses ?? null,
                limit: opts?.limit ?? 50,
            });
            this.jobs.set(res.jobs);
            this.jobsActive.set(res.active);
            this.jobsLoaded.set(true);
        } catch {
            this.jobs.set([]);
            this.jobsActive.set(0);
            this.jobsLoaded.set(false);
        }
    }

    /**
     * Probe `POST /vendors/balance` (admin auth) and update `vendorBalances` /
     * `vendorBalancesLoaded`. Pass `force` to bypass the service-side cache on
     * an explicit operator refresh. Never throws — a failed probe clears the
     * snapshot and marks `vendorBalancesLoaded` false.
     */
    async refreshVendorBalances(force = false): Promise<void> {
        this.vendorBalancesLoading.set(true);
        try {
            const res = await this.post<VendorBalancesResponse>('vendors/balance', { force });
            this.vendorBalances.set(res);
            this.vendorBalancesLoaded.set(true);
        } catch {
            this.vendorBalances.set(null);
            this.vendorBalancesLoaded.set(false);
        } finally {
            this.vendorBalancesLoading.set(false);
        }
    }

    /** The latest balance snapshot for a single vendor, or null if unseen. */
    balanceFor(vendor: string): VendorBalance | null {
        return this.vendorBalances()?.vendors.find((v) => v.vendor === vendor) ?? null;
    }

    /**
     * Vendors currently `critical` (out of credits) from the latest snapshot.
     * The service is the authoritative gate; this is a UX pre-check so the
     * operator sees *why* a generation is disabled. Fails open: returns `[]`
     * when balances are unknown, matching the server's behaviour.
     */
    blockedVendors(): string[] {
        return (
            this.vendorBalances()
                ?.vendors.filter((v) => v.status === 'critical')
                .map((v) => v.vendor) ?? []
        );
    }

    /**
     * Rewrite / improve operator-supplied text via Grok (`POST /text/refine`).
     * Sync (< ~8 s service-side). Generates the per-call `request_id` itself.
     * Throws `AiAssistApiError` on the service's 400 error envelope.
     */
    async refine(req: RefineRequest): Promise<RefineResponse> {
        return this.post<RefineResponse>('text/refine', {
            request_id: crypto.randomUUID(),
            text: req.text,
            instruction: req.instruction,
            max_length: req.maxLength ?? null,
        });
    }

    /**
     * Draft SEO meta-description options via Grok (`POST /text/suggest-branding`).
     * Sync. Generates the per-call `request_id` itself. Throws `AiAssistApiError`
     * on the service's 400 error envelope.
     */
    async suggestBranding(req: SuggestBrandingRequest): Promise<SuggestBrandingResponse> {
        return this.post<SuggestBrandingResponse>('text/suggest-branding', {
            request_id: crypto.randomUUID(),
            brand_name: req.brandName,
            domain: req.domain,
            vertical: req.vertical ?? null,
            style: req.style ?? null,
            context: {
                display_name: req.context?.displayName ?? null,
                name: req.context?.name ?? null,
            },
            operator_note: req.operatorNote ?? null,
        });
    }

    /**
     * Kick off text-free base-art generation (`POST /asset/base/generate`).
     * Async: returns 202 + `job_id`; poll the result with `awaitJob(job_id)`.
     */
    async generateBaseArt(req: BaseArtGenerateRequest): Promise<JobAccepted> {
        return this.post<JobAccepted>('asset/base/generate', {
            request_id: crypto.randomUUID(),
            job_id: req.jobId,
            realm_id: req.realmId ?? null,
            template_id: req.templateId,
            version: req.version,
            style_descriptor: req.styleDescriptor ?? {},
            reference_image_urls: req.referenceImageUrls ?? [],
            reference_images_b64: req.referenceImagesB64 ?? [],
            asset_type: req.assetType,
            game_template_id: req.gameTemplateId ?? null,
            game_kind: req.gameKind ?? null,
            game_meta: req.gameMeta ?? null,
            mode: req.mode ?? 'auto',
            prompt: req.prompt ?? null,
            operator_note: req.operatorNote ?? null,
            samples: req.samples ?? 4,
        });
    }

    /**
     * Kick off name-overlay finalization (`POST /asset/finalize`) — e.g. the
     * realm OG image from a base plate. Async: returns 202 + `job_id`.
     */
    async finalizeImage(req: FinalizeImageRequest): Promise<JobAccepted> {
        return this.post<JobAccepted>('asset/finalize', {
            request_id: crypto.randomUUID(),
            job_id: req.jobId,
            realm_id: req.realmId ?? null,
            base_plate_url: req.basePlateUrl ?? null,
            base_plate_b64: req.basePlateB64 ?? null,
            asset_type: req.assetType,
            title: req.title,
            tagline: req.tagline ?? null,
            style_descriptor: req.styleDescriptor ?? null,
            lettering_url: req.letteringUrl ?? null,
            lettering_b64: req.letteringB64 ?? null,
            title_seed: req.titleSeed ?? null,
            samples: req.samples ?? 4,
        });
    }

    /**
     * Generate storyboard pages (Contract 3, `POST /asset/pages/generate`).
     * Async: returns 202 + `job_id`; poll with `awaitJob` and read `PagesResult`.
     */
    async generatePages(req: PagesGenerateRequest): Promise<JobAccepted> {
        return this.post<JobAccepted>('asset/pages/generate', {
            request_id: crypto.randomUUID(),
            job_id: req.jobId,
            realm_id: req.realmId ?? null,
            template_id: req.templateId,
            version: req.version,
            style_descriptor: req.styleDescriptor ?? {},
            reference_image_urls: req.referenceImageUrls ?? [],
            reference_images_b64: req.referenceImagesB64 ?? [],
            game_base_urls: req.gameBaseUrls ?? [],
            game_base_b64: req.gameBaseB64 ?? [],
            lettering_url: req.letteringUrl ?? null,
            lettering_b64: req.letteringB64 ?? null,
            game_template_id: req.gameTemplateId,
            game_kind: req.gameKind,
            game_meta: req.gameMeta ?? null,
            title: req.title,
            scene_mode: req.sceneMode ?? 'auto',
            page_count: req.pageCount ?? 3,
            copy_mode: req.copyMode ?? 'auto',
            samples: req.samples ?? 2,
        });
    }

    /** Single poll of an async job's status (`POST /asset/jobs/{job_id}/status`). */
    async pollJob(jobId: string): Promise<JobStatusResponse> {
        return this.post<JobStatusResponse>(`asset/jobs/${jobId}/status`, {
            request_id: crypto.randomUUID(),
        });
    }

    /**
     * Poll an async job until it terminates. Resolves with the completed
     * `JobStatusResponse` (read `result`), or throws `AiAssistApiError` on a
     * failed / cancelled job, a timeout (`vendor_timeout`), or abort.
     */
    async awaitJob(
        jobId: string,
        opts: {
            intervalMs?: number;
            timeoutMs?: number;
            signal?: AbortSignal;
        } = {},
    ): Promise<JobStatusResponse> {
        const intervalMs = opts.intervalMs ?? 2500;
        const timeoutMs = opts.timeoutMs ?? 180_000;
        const deadline = Date.now() + timeoutMs;

        for (;;) {
            if (opts.signal?.aborted) {
                throw new AiAssistApiError('internal_error', 'Polling aborted');
            }
            const res = await this.pollJob(jobId);
            if (res.status === 'completed') return res;
            if (res.status === 'failed') {
                throw new AiAssistApiError(res.error?.code ?? 'internal_error', res.error?.message);
            }
            if (res.status === 'cancelled') {
                throw new AiAssistApiError('internal_error', 'The job was cancelled.');
            }
            if (Date.now() >= deadline) {
                throw new AiAssistApiError(
                    'vendor_timeout',
                    'Timed out waiting for the image job to finish.',
                );
            }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
    }

    /**
     * POST a JSON body to a service endpoint. Attaches the admin Bearer token by
     * default (every endpoint except `/health` requires it). Throws
     * `AiAssistApiError` on the service's 400 error envelope.
     *
     * The token comes from `AuthService.getFreshAuthToken()`, which refreshes it
     * through the lib session when it is close to expiry — required here because
     * these requests bypass `SessionAdminApiClient` and would otherwise run on a
     * stale stored token during long job-polling stretches. A rejection with the
     * service's `unauthorized` code still gets one forced-refresh retry to cover
     * expiry races around the refresh threshold.
     */
    async post<T>(path: string, body: unknown, opts: { auth?: boolean } = {}): Promise<T> {
        if (opts.auth === false) {
            return this.doPost<T>(path, body, null);
        }

        const token = await this.auth.getFreshAuthToken();
        if (!token) {
            throw new AiAssistApiError('unauthorized', 'No admin session token');
        }

        try {
            return await this.doPost<T>(path, body, token);
        } catch (e) {
            if (!(e instanceof AiAssistApiError) || e.code !== 'unauthorized') {
                throw e;
            }
            const fresh = await this.auth.getFreshAuthToken({
                forceRefresh: true,
            });
            // No retry when the refresh produced nothing new — the session is
            // gone (rethrow → caller surfaces auth error; login redirect has
            // already happened) or the service rejects a token the session
            // still considers valid (retrying would loop on the same error).
            if (!fresh || fresh === token) throw e;
            return this.doPost<T>(path, body, fresh);
        }
    }

    private async doPost<T>(path: string, body: unknown, token: string | null): Promise<T> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${this.baseUrl}${path}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            cache: 'no-store',
        });

        const data: unknown = await response.json().catch(() => null);

        if (!response.ok) {
            const err = (data ?? {}) as AiAssistErrorBody;
            throw new AiAssistApiError(err.code ?? 'internal_error', err.message);
        }

        return data as T;
    }
}
